import React, { useEffect, useState, FC } from 'react'
import Skeleton from '@ynap/skeleton'
import BEMHelper from '@ynap/bem-helper'

const bem = new BEMHelper('PromotionCheck')

type Promotion = {
    id: string
    name: string
    description: string
    checkerInfo?: CheckerInfo
}

type CheckerInfo = {
    valid: boolean
    reasons?: Record<string, string | undefined>
    loading: boolean
}

type ActivePromotions = {
    promotions?: Array<Promotion>
    error?: string
    isLoadingPromotions: boolean
}

type Props = {
    activePromotions: ActivePromotions
    checkPromotion(obj: { id: string }): void
    fetchPromotions?(): void
    messages: any
    promotionsCheckerInfo?: Record<string, CheckerInfo>
}

export const PromotionCheck: FC<Props> = (props) => {
    const { activePromotions, fetchPromotions, checkPromotion, promotionsCheckerInfo, messages } = props
    const { promotions, error, isLoadingPromotions = true } = activePromotions || {}
    const [activePromotion, setActivePromotion] = useState<string | undefined>(undefined)

    const promotionsWithInfo = promotions?.map((promotion) => ({
        ...promotion,
        checkerInfo: promotionsCheckerInfo?.[promotion.id],
        loading: !!promotionsCheckerInfo?.[promotion.id]?.loading,
    }))

    useEffect(() => {
        fetchPromotions?.()
    }, [])

    if (isLoadingPromotions) return <Skeleton count={5} />

    if (error) return <span className={bem('promotion-checker-info')}> {messages.errors.global()} </span>

    return (
        <div className={bem()}>
            <ul>
                {promotionsWithInfo?.map((promotion) => {
                    const active = promotion.id === activePromotion
                    const handleClick = () => {
                        if (active) return
                        setActivePromotion(promotion.id)
                        checkPromotion({ id: promotion.id })
                    }
                    return (
                        <li key={promotion.id}>
                            <PromotionItem active={active} onClick={handleClick} {...promotion} messages={messages} />
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

type PromotionItemProps = {
    active: boolean
    onClick(): void
    messages: any
    loading: boolean
} & Promotion

const PromotionItem: FC<PromotionItemProps> = (props) => {
    const { active, name, description, checkerInfo, onClick, loading, messages } = props
    const valid = !!checkerInfo?.valid 
    const reasons = checkerInfo?.reasons ?? {}

    const sortOtherReasonsLast = ([key], _) => key === 'otherReasons' ? +1 : -1
    const sortedReasons = Object.entries(reasons).sort(sortOtherReasonsLast).map(([, value]) => value);
    const failedReasons = sortedReasons.length ? sortedReasons : [messages.errors.global()]

    return (
        <div className={bem('promotion-item')}>
            <span className={bem('title', { active })} onClick={onClick}>
                {name}
            </span>
            {active &&
                (loading ? (
                    <Skeleton className={bem('content')} count={2} />
                ) : (
                    <div className={bem('content')}>
                        {valid ? (
                            <span className={bem('promotion-checker-info', 'valid')}>{messages.notifications.promotionValid()}</span>
                        ) : (
                            <ul className={bem('promotion-checker-info')}>
                                { 
                                failedReasons.map((failedReason, index) => (
                                    <li key={index}>
                                        { failedReason }
                                    </li>
                                 ))
                             }</ul>
                        )}
                        <span className={bem('description')}>{description}</span>
                    </div>
                ))}
        </div>
    )
}

export default PromotionCheck
